import {
  useCallback, useEffect, useState, MouseEvent,
} from 'react';
import {
  ApiCheckUnlockPay,
  ApplicantStatusEnum,
  GenericV1ErrorsArray,
  UserTalentInterviewFolderItem,
  ApiGetOperationJobOk,
} from '@type/v1-api-types';
import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';
import {
  showUpgradeSubscriptionModal,
} from '@components/features/modals/employer/subscription/UpgradeSubscriptionModal';
import { useStaticToasts } from '@hooks/useStaticToasts';
import { useSwrResumeCheckUnlock, useSwrResumeGetOperationJob, useSwrResumeSetStatus } from '@hooks/useSwrApi/endpoints/resume-operations';
import { useSwrUserResumeDetail } from '@hooks/useSwrApi/endpoints/user';
import { useOpenTokCallStart } from '@components/features/openTokCall/useOpenTokCallStart';
import { useBjModal } from '@hooks/common/useBjModal';
import { showCvContactModal } from '@components/features/modals/employer/CvContactModal';
import { isCheckUnlockStatusPayOk } from 'src/type-predicates/v1-api-predicates/resume-operations';
import { useModalHistory } from '@hooks/history/useModalHistory';
import { showAddCompanyInfoModal } from '@components/features/modals/employer/AddCompanyInfoModal';
import { showSetCvStatusModal } from '@components/features/modals/employer/cv/SetCvStatusModal';

// Interface
// **************************************
interface TalentCardActionsProps {
  userSlug: string;
  locale: Locale;
  rid?: string;
  jobSlug?: string;
  jobTitle?: string;
  jobFolderId?: number;
  revalidatePage: (userSlug: string) => void;
  hasInterviewFolder: boolean;
  interviewFolders?: UserTalentInterviewFolderItem[];
  trackingId?: string;
  handleStatusChangeOnCard?: (statusId: string | number, cvSlug: string) => void;
  isPassiveRecommendation?: boolean,
}

interface HandleCvStatusModalParams {
  operationJobData?: ApiGetOperationJobOk,
  statusId: ApplicantStatusEnum,
  googleTagEventName?: string,
  conditionallyDisableSubmit?: boolean,
  onCloseAction?: () => void,
  title?: string,
  successToastText?: string,
  keepExistingStatus?: boolean,
}

/**
 * @description Talent Card Actions hook. Used for actions that are displayed on a talent card.
 * The possible actions are: Send a message, Call 1-1, Select for the interview.
 * * **userSlug** - The user slug.
 * * **locale** - The locale to use for SWR hooks.
 * * **rid** - optional. Reference ID. Used for tracking purposes.
 * * **jobSlug** - optional. The job's slug.
 * * **jobTitle** - optional. The job title.
 * * **jobFolderId** - optional. The job folder id.
 * * **revalidatePage** - Callback function to be called to revalidate SWR Infinite List Data.
 * * **hasInterviewFolder** - Whether the talent has an interview folder or not (the data that comes from the server).
 * * **interviewFolders** - optional. An array that contains the jobs where the talent already has the status Selected for the interview.
 * * **handleStatusChangeOnCard** - optional. Function to handle the status change on the card for Drag and Drop lists (will move the card accordingly).
 */
export const useTalentCardActions = (props: TalentCardActionsProps) => {
  // Destructure props
  const {
    userSlug,
    locale,
    rid,
    jobSlug,
    jobTitle,
    jobFolderId,
    revalidatePage,
    hasInterviewFolder,
    interviewFolders,
    handleStatusChangeOnCard,
    isPassiveRecommendation,
  } = props;

  const messageInJobContextModalTitle = 'talent-card.v2.action.message.with.job.folder.modal.title';
  const toastMessageInJobContext = 'toast.message.job.successfully.selected';

  const { showStatusToast, statusGenericErrorToast } = useStaticToasts();

  const [canSendMessage, setCanSendMessage] = useState<boolean>(false);
  const { isOpen: displaySendMessageModal, toggleModal: toggleSendMessageModal } = useBjModal();
  const { isOpen: displayOpenCvDetail, toggleModal: toggleDisplayCvDetail } = useBjModal();

  const [unlockedByCall, setUnlockedByCall] = useState <boolean>(false);
  const [unlockedByContactData, setUnlockedByContactData] = useState <boolean>(false);

  const [shouldFetchResumeDetail, setShouldFetchResumeDetail] = useState <boolean>(false);
  const [showVideoOptions, setShowVideoOptions] = useState <boolean>(false);

  // If we should open the message modal
  const [shouldOpenMessageModal, setShouldOpenMessageModal] = useState<boolean>(false);
  // saved operationJob data
  const [savedOperationJob, setSavedOperationJob] = useState<ApiGetOperationJobOk | undefined>(undefined);

  // start video call options
  const { onShowVideoOptions } = useOpenTokCallStart();

  const [currentOperationJobId, setCurrentOperationJobId] = useState<number | undefined>(undefined);

  const { updateModalHistory } = useModalHistory();

  // Generic error callback for unlock action
  // ******************************************************
  const unlockActionGenericErrorCallback = (errors: GenericV1ErrorsArray | ApiCheckUnlockPay) => {
    if ('errors' in errors) {
      const { code } = errors.errors[0];

      // If the error status code is 601 (not enough credits), we show the upgrade subscription modal.
      // Otherwise, we show a toast with the error message.
      if (code === 601) {
        showUpgradeSubscriptionModal();
      } else if (code !== 701 && code !== 403) {
        showStatusToast(String(`${code}`));
      }
    } else if (isCheckUnlockStatusPayOk(errors)) {
      showCvContactModal(errors?.id, locale);
    }
  };

  // SWR: Get User Resume Detail.
  // ******************************************************
  const {
    data: resumeDetail,
    isLoading: isLoadingResumeDetail,
  } = useSwrUserResumeDetail({
    locale,
    userSlug,
    shouldFetch: shouldFetchResumeDetail,
  });

  // SWR: Set Shortlist Status in job context.
  // ******************************************************

  const {
    trigger: setJobContextShortlistStatus,
    isMutating: isLoadingJobContextShortlistStatus,
  } = useSwrResumeSetStatus({
    userSlug,
    status: ApplicantStatusEnum.SHORTLIST,
    locale,
    successCallback: () => {
      // move DragAndDrop on Flow board
      handleStatusChangeOnCard?.(ApplicantStatusEnum.SHORTLIST, userSlug);
    },
  });

  // SWR mutation: Unlock for Message
  // ******************************************************
  const {
    trigger: resumeCheckUnlockWithMessageTrigger,
    isMutating: isLoadingSendMessage,
  } = useSwrResumeCheckUnlock({
    userSlug,
    type: 'message',
    locale,
    rid,
    isPassiveRecommendation: Boolean(isPassiveRecommendation),
    successCallback: () => {
      toggleSendMessageModal();
      if (revalidatePage) { revalidatePage(userSlug); }

      setCanSendMessage(true);

      sendGoogleTagEvent('cv_list_premium_contact_message', 'Custom');
    },
    errorCallback: (errors) => {
      unlockActionGenericErrorCallback(errors);
      const { code } = errors.errors[0];
      if (code === 403) {
        const successCallback = () => {
          void resumeCheckUnlockWithMessageTrigger({
            jobId: jobFolderId,
            title: jobTitle,
            ...(jobSlug ? { jobSlug } : {}),
          });
        };
        showAddCompanyInfoModal(locale, successCallback);
      }
    },
  });

  // SWR mutation: Unlock for contact with call
  // ******************************************************
  const {
    trigger: resumeCheckUnlockByCallTrigger,
    isMutating: isLoadingCallNow,
  } = useSwrResumeCheckUnlock({
    userSlug,
    type: 'contact',
    locale,
    rid,
    successCallback: () => {
      if (revalidatePage) { revalidatePage(userSlug); }
      setUnlockedByCall(true);
      setShouldFetchResumeDetail(true);
      setShowVideoOptions(true);
      sendGoogleTagEvent('cv_list_premium_contact_call', 'Custom');
    },
    errorCallback: (errors) => {
      unlockActionGenericErrorCallback(errors);
      const { code } = errors.errors[0];
      if (code === 403) {
        const successCallback = () => {
          void resumeCheckUnlockByCallTrigger({
            jobId: jobFolderId,
            title: jobTitle,
            ...(jobSlug ? { jobSlug } : {}),
          });
        };
        showAddCompanyInfoModal(locale, successCallback);
      }
    },
  });


  // SWR mutation: Unlock for contact with call
  // ******************************************************
  const {
    trigger: resumeCheckUnlockByContactData,
    isMutating: isLoadingContactData,
  } = useSwrResumeCheckUnlock({
    userSlug,
    type: 'contact',
    locale,
    rid,
    successCallback: () => {
      if (revalidatePage) { revalidatePage(userSlug); }
      updateModalHistory({
        pathname: `/cv/${userSlug}`,
      });
      toggleDisplayCvDetail();
      setUnlockedByContactData(true);
    },
    errorCallback: (errors) => {
      unlockActionGenericErrorCallback(errors);
      const { code } = errors.errors[0];
      if (code === 403) {
        const successCallback = () => {
          void resumeCheckUnlockByContactData({
            jobId: jobFolderId,
            title: jobTitle,
            ...(jobSlug ? { jobSlug } : {}),
          });
        };
        showAddCompanyInfoModal(locale, successCallback);
      }
    },
  });


  // Helper for handling the CvStatusModal
  // ******************************************************
  const handleCvStatusModal = useCallback((params: HandleCvStatusModalParams) => {
    const {
      operationJobData,
      statusId,
      googleTagEventName,
      conditionallyDisableSubmit,
      title,
      successToastText,
      onCloseAction,
      keepExistingStatus,
    } = params;

    showSetCvStatusModal({
      locale,
      operationJob: operationJobData,
      userSlug,
      interviewFolders,
      status: statusId,
      trackingId: rid,
      conditionallyDisableSubmit,
      title,
      successToastText,
      keepExistingStatus,
      onCloseCallback: (isSuccess: boolean) => {
        if (isSuccess) {
          if (googleTagEventName) sendGoogleTagEvent(googleTagEventName, 'Custom');
          if (revalidatePage) revalidatePage(userSlug);
          if (onCloseAction) onCloseAction();
        }
      },
    });
  }, [interviewFolders, locale, rid, revalidatePage, userSlug]);


  // Unlock with message
  // ******************************************************
  const unlockWithMessage = useCallback(() => {
    if (!userSlug) {
      statusGenericErrorToast();
      return;
    }

    if (canSendMessage) {
      toggleSendMessageModal();
    } else {
      void resumeCheckUnlockWithMessageTrigger({
        jobId: jobFolderId || (savedOperationJob?.jobId ?? jobFolderId),
        title: jobTitle || (savedOperationJob?.title ?? jobTitle),
        jobSlug: jobSlug || (savedOperationJob?.jobSlug ?? jobSlug),
      });
    }
  }, [
    canSendMessage,
    jobSlug, jobFolderId,
    jobTitle,
    resumeCheckUnlockWithMessageTrigger,
    statusGenericErrorToast,
    toggleSendMessageModal,
    userSlug,
    savedOperationJob,
  ]);

  // Unlock by call
  // ******************************************************
  const unlockByCall = () => {
    if (!userSlug) {
      statusGenericErrorToast();
    }

    if (unlockedByCall) {
      setShowVideoOptions(true);
    } else {
      void resumeCheckUnlockByCallTrigger({
        jobId: jobFolderId,
        title: jobTitle,
        ...(jobSlug ? { jobSlug } : {}),
      });
    }
  };

  // Unlock by contact data
  // ******************************************************
  const unlockByContactData = () => {
    if (!userSlug) {
      statusGenericErrorToast();
    }
    if (unlockedByContactData) {
      toggleDisplayCvDetail();
    } else {
      void resumeCheckUnlockByContactData({
        jobId: jobFolderId,
        title: jobTitle,
        ...(jobSlug ? { jobSlug } : {}),
      });
    }
  };


  useEffect(() => {
    // show video options after unlocking the cv
    // add trackingId to the event if it exists
    if (resumeDetail?.id && unlockedByCall && showVideoOptions) {
      onShowVideoOptions({
        userId: resumeDetail?.id,
        userName: resumeDetail.privateInformation?.name ?? '',
        slug: resumeDetail.slug,
        imgUrl: resumeDetail.imgUrl,
        email: resumeDetail.privateInformation?.email,
        phone: resumeDetail.privateInformation?.phone,
        ...(rid && { trackingId: rid }),
      });
      setShowVideoOptions(false);
    }
  }, [resumeDetail, unlockedByCall, statusGenericErrorToast, onShowVideoOptions, showVideoOptions, rid]);


  // SWR mutation: Get Operation Job
  // ******************************************************
  const {
    data: operationJob,
    trigger: getOperationJobTrigger,
    isMutating: isLoadingOperationJob,
  } = useSwrResumeGetOperationJob({
    jobId: currentOperationJobId,
    locale,
    successCallback: (operationJobData) => {
      setSavedOperationJob(operationJobData);
      setTimeout(() => {
        handleCvStatusModal({
          operationJobData,
          statusId: shouldOpenMessageModal ? ApplicantStatusEnum.VIEWED : ApplicantStatusEnum.SHORTLIST,
          googleTagEventName: shouldOpenMessageModal ? 'cv_list_set_attempt_message' : 'cv_list_set_shortlist',
          conditionallyDisableSubmit: !shouldOpenMessageModal,
          ...(shouldOpenMessageModal && { keepExistingStatus: true }),
          ...(shouldOpenMessageModal && { title: messageInJobContextModalTitle }),
          ...(shouldOpenMessageModal && { successToastText: toastMessageInJobContext }),
          onCloseAction: () => {
            if (shouldOpenMessageModal) unlockWithMessage();
            setShouldOpenMessageModal(false);
          },
        });
      }, 0);
    },
  });


  // Save to Shortlist action
  // ******************************************************
  const saveToShortlistAction = useCallback((e?: MouseEvent<HTMLButtonElement>) => {
    // If jobSlug is defined, we can change the status directly, we're in the job context.
    if (jobSlug) {
      // This is needed for Drag and Drop flow board, so it will actually register the click event and trigger the defaultPrevented
      // inside the getClickedItemRubric function.
      e?.preventDefault();
      void setJobContextShortlistStatus({ jobId: jobFolderId, title: jobTitle });
    } else {
      // If jobSlug is not defined, we need to open the job folders modal first.
      setShouldOpenMessageModal(false);
      if (hasInterviewFolder && interviewFolders?.length) {
        const displayedFolder = interviewFolders
          .find((item) => (item.statusId as ApplicantStatusEnum) === ApplicantStatusEnum.SHORTLIST);
        if (!displayedFolder) {
          // wait for the next tick to fetch the operation job
          setTimeout(() => { void getOperationJobTrigger(); }, 0);
        } else if (displayedFolder.jobId === operationJob?.jobId) {
          handleCvStatusModal({
            operationJobData: operationJob,
            statusId: ApplicantStatusEnum.SHORTLIST,
            googleTagEventName: 'cv_list_set_shortlist',
          });
        } else {
          setCurrentOperationJobId(displayedFolder.jobId);
        }
      } else if (operationJob) {
        handleCvStatusModal({
          operationJobData: operationJob,
          statusId: ApplicantStatusEnum.SHORTLIST,
          googleTagEventName: 'cv_list_set_shortlist',
        });
      } else {
        // wait for the next tick to fetch the operation job
        setTimeout(() => { void getOperationJobTrigger(); }, 0);
      }
    }
  }, [
    getOperationJobTrigger, hasInterviewFolder, interviewFolders, operationJob, handleCvStatusModal, jobSlug,
    jobFolderId, jobTitle, setJobContextShortlistStatus,
  ]);

  // It will open the job folders modal before opening the message modal.
  // ******************************************************
  const openMessageWithJobFolder = useCallback(() => {
    if (jobSlug) {
      // If jobSlug is defined, we can 'unlock with message' directly, we're in the job context.
      unlockWithMessage();
    } else {
      // If jobSlug is not defined, we need to open the job folders modal first.
      setShouldOpenMessageModal(true);
      if (hasInterviewFolder && interviewFolders?.length) {
        const displayedFolder = interviewFolders
          .find((item) => (item.statusId as ApplicantStatusEnum) === ApplicantStatusEnum.VIEWED);
        if (!displayedFolder) {
          // wait for the next tick to fetch the operation job
          setTimeout(() => { void getOperationJobTrigger(); }, 0);
        } else if (displayedFolder.jobId === operationJob?.jobId) {
          handleCvStatusModal({
            operationJobData: operationJob,
            statusId: ApplicantStatusEnum.VIEWED,
            googleTagEventName: 'cv_list_set_attempt_message',
            conditionallyDisableSubmit: false,
            title: messageInJobContextModalTitle,
            successToastText: toastMessageInJobContext,
            keepExistingStatus: true,
            onCloseAction: () => {
              unlockWithMessage();
              setShouldOpenMessageModal(false);
            },
          });
        } else {
          setCurrentOperationJobId(displayedFolder.jobId);
        }
      } else if (operationJob) {
        handleCvStatusModal({
          operationJobData: operationJob,
          statusId: ApplicantStatusEnum.VIEWED,
          googleTagEventName: 'cv_list_set_attempt_message',
          conditionallyDisableSubmit: false,
          title: messageInJobContextModalTitle,
          successToastText: toastMessageInJobContext,
          keepExistingStatus: true,
          onCloseAction: () => {
            unlockWithMessage();
            setShouldOpenMessageModal(false);
          },
        });
      } else {
        // wait for the next tick to fetch the operation job
        setTimeout(() => { void getOperationJobTrigger(); }, 0);
      }
    }
  }, [getOperationJobTrigger, hasInterviewFolder, interviewFolders, operationJob, handleCvStatusModal, unlockWithMessage, jobSlug]);

  useEffect(() => {
    if (currentOperationJobId) {
      void getOperationJobTrigger();
    }
  }, [currentOperationJobId, getOperationJobTrigger]);

  // Return data
  // ********************************
  return {
    canSendMessage,
    displaySendMessageModal,
    toggleSendMessageModal,
    displayOpenCvDetail,
    toggleDisplayCvDetail,
    isLoadingSendMessage,
    unlockWithMessage,
    unlockByCall,
    unlockByContactData,
    isLoadingCallNow,
    isLoadingResumeDetail,
    isLoadingContactData,
    currentOperationJobId,
    setCurrentOperationJobId,
    operationJob,
    isLoadingOperationJob,
    saveToShortlistAction,
    openMessageWithJobFolder,
    shouldOpenMessageModal,
    isLoadingJobContextShortlistStatus,
    isPassiveRecommendation,
  };
};
